import { useEffect, useState, type ReactNode } from 'react';
import {
  RUNTIME_REQUEST_STATE_EVENT,
  type RuntimeRequestIssue,
  type RuntimeRequestState,
} from '@/shared/runtime/runtimeRequestState';
import styles from '@/shared/ui/RuntimeStateBoundary.module.css';

type Copy = { title: string; description: string; action: string };

const COPY: Record<RuntimeRequestIssue, Copy> = {
  offline: {
    title: 'Нет соединения',
    description: 'Можно просматривать уже загруженные данные. Для изменений восстановите сеть и обновите данные.',
    action: 'Проверить соединение',
  },
  timeout: {
    title: 'Ответ занимает больше времени',
    description: 'Операция не считается повторённой. Проверьте её состояние и безопасно повторите загрузку.',
    action: 'Повторить',
  },
  access_denied: {
    title: 'Нет доступа к этому разделу',
    description: 'Войдите через подключённый Telegram или MAX и проверьте выбранную организацию.',
    action: 'Открыть заново',
  },
  expired: {
    title: 'Ссылка или сессия истекла',
    description: 'Получите новую безопасную ссылку либо снова откройте витрину из бота.',
    action: 'Открыть заново',
  },
  stale: {
    title: 'Данные могли измениться',
    description: 'Обновите страницу перед подтверждением цены, наличия или следующего действия.',
    action: 'Обновить данные',
  },
  unavailable: {
    title: 'Сервис временно не отвечает',
    description: 'Сначала проверьте состояние операции, затем повторите её: сервер защитит повтор от дублирования.',
    action: 'Повторить',
  },
};

export function RuntimeStateBoundary({ children }: { children: ReactNode }): JSX.Element {
  const [issue, setIssue] = useState<RuntimeRequestIssue | null>(() => (
    typeof navigator !== 'undefined' && !navigator.onLine ? 'offline' : null
  ));

  useEffect(() => {
    const handleOffline = (): void => setIssue('offline');
    const handleOnline = (): void => setIssue((current) => current === 'offline' ? 'stale' : current);
    const handleRequestState = (event: Event): void => {
      const state = (event as CustomEvent<RuntimeRequestState>).detail;
      if (!state) return;
      if (state.kind === 'recovered') {
        setIssue((current) => current === 'expired' ? current : null);
      }
      else setIssue(state.kind);
    };
    window.addEventListener('offline', handleOffline);
    window.addEventListener('online', handleOnline);
    window.addEventListener(RUNTIME_REQUEST_STATE_EVENT, handleRequestState);
    return () => {
      window.removeEventListener('offline', handleOffline);
      window.removeEventListener('online', handleOnline);
      window.removeEventListener(RUNTIME_REQUEST_STATE_EVENT, handleRequestState);
    };
  }, []);

  if (issue === 'expired') {
    const copy = COPY[issue];
    return (
      <main className={styles.blocking} role="alert" aria-live="assertive" data-first-screen-ready="true">
        <strong>{copy.title}</strong>
        <span>{copy.description}</span>
        <button type="button" onClick={() => window.location.reload()}>{copy.action}</button>
      </main>
    );
  }

  return (
    <>
      {issue ? (
        <aside className={styles.banner} role="status" aria-live="polite">
          <strong>{COPY[issue].title}</strong>
          <span>{COPY[issue].description}</span>
          <button type="button" onClick={() => window.location.reload()}>{COPY[issue].action}</button>
        </aside>
      ) : null}
      {children}
    </>
  );
}
